import axios, { CancelTokenSource } from 'axios';
import { AppModule } from '@/store/modules/app';
import { API_BASE_URL } from '@/config/constant';
import { groupProfessionQueryTsp } from '@/config/configuration';

export interface ProfessionOption {
  id: string
  name: string
  title?: string
  count: number
}

export interface ProfessionGroup {
  group: string
  libs: ProfessionOption[]
}

export interface ProfessionQueryOptions {
  isApplicantSearch?: boolean
  courses?: boolean
}

// Composite ids come as "~71394~71393"; the search params expect "71394,71393"
export const normalizeProfessionId = (id: unknown): string => String(id ?? '')
  .split('~')
  .filter((part) => part)
  .join(',');

let pending: CancelTokenSource | null = null;

// Occupation autocomplete (jobnews/profession_query_new, or _tsp for the TSP sids), grouped
// by parent occupation. A new query cancels the previous one; a cancelled call resolves to null.
export const queryProfessions = async(query: string, options: ProfessionQueryOptions = {}): Promise<ProfessionGroup[] | null> => {
  pending?.cancel();
  pending = axios.CancelToken.source();
  const path = groupProfessionQueryTsp(AppModule.sid) ? 'jobnews/profession_query_tsp' : 'jobnews/profession_query_new';
  try {
    const { data } = await axios.get(`${API_BASE_URL}/${path}`, {
      params: {
        sid: AppModule.languageSid,
        q: query,
        ...(options.isApplicantSearch && { applicant_search: 1 }),
        ...(options.courses && { course_search: 1 }),
      },
      cancelToken: pending.token,
    });
    const groups: ProfessionGroup[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const item of data) {
      // A "message" item means the query has no usable result
      if (item.message) return [];
      const title = item.parent_name || AppModule.translation[11483];
      let group = groups.find((entry) => entry.group === title);
      if (!group) {
        group = { group: title, libs: [] };
        groups.push(group);
      }
      group.libs.push({
        id: normalizeProfessionId(item.id),
        name: item.name,
        count: parseInt(item.count, 10),
        title: item.title,
      });
    }
    return groups;
  } catch (error) {
    if (axios.isCancel(error)) return null;
    throw error;
  }
};
